<template>
  <label
    class="toggle-button"
    :class="[`toggle-button--${type}`, variant && `toggle-button--${variant}`]"
  >
    <input
      class="toggle-button__input"
      type="checkbox"
      :checked="value"
      @change="notifyChange"
    />
    <span class="toggle-button__slider"></span>
    <span
      v-if="$slots.default || type === 'checkbox'"
      class="toggle-button__label"
    >
      <slot></slot>
    </span>
  </label>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    value: boolean
    type?: 'checkbox' | 'toggle'
    /**
     * Visual variant. `menu`: the checkbox style used in menu lists, such as
     * the Save destination menu.
     */
    variant?: 'menu'
  }>(),
  {
    type: 'toggle',
  },
)

const emit = defineEmits(['update:value'])

function notifyChange(event: Event): void {
  // This event must match the name of the `value` prop
  // so that `v-model` can be used.
  emit('update:value', (event.target as HTMLInputElement)?.checked)
}
</script>

<style lang="scss" scoped>
.toggle-button {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &__label {
    font-size: 18px;
    user-select: none;
  }
}

.toggle-button--toggle .toggle-button {
  &__label {
    @media (max-width: $w-mobile-s) {
      font-size: 16px;
    }
  }

  /* Slider head. */
  &__slider {
    position: relative;
    width: 44px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 17px;
    background-color: #d5d8e0;
    -webkit-transition: 0.1s;
    transition: 0.1s;

    /* Slider background. */
    &:before {
      position: absolute;
      height: 22px;
      width: 22px;
      left: 2px;
      bottom: 2px;

      border-radius: 50%;
      background-color: white;
      content: '';

      -webkit-transition: 0.1s;
      transition: 0.1s;
    }
  }

  /* Hide default HTML checkbox. */
  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;

    /* A checked box colors and offsets the slider. */
    &:checked {
      + .toggle-button__slider {
        background-color: #ffc906;
      }

      + .toggle-button__slider:before {
        -webkit-transform: translateX(18px);
        -ms-transform: translateX(18px);
        transform: translateX(18px);
      }
    }
  }
}

.night-mode .toggle-button--toggle .toggle-button {
  &__slider {
    background-color: $c-dark-8;
  }
}

.toggle-button--checkbox .toggle-button {
  &__slider {
    display: none;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;

    &:before {
      content: '';
      display: flex;
      justify-content: center;
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      border: 1px solid $c-black-6;
      border-radius: 4px;
      margin-top: -4px;
      background: transparent;
      transition: all 0.15s ease-in-out;
    }
  }

  &__input {
    position: absolute;
    z-index: -1;
    appearance: none;
    background: transparent;
    margin: 0;

    &:checked ~ .toggle-button__label:before {
      content: url('@/assets.app/images/onboarding/check.svg');
      background: $c-yellow;
      border-color: $c-yellow;
    }
  }
}

.toggle-button--menu .toggle-button__label {
  font-size: 16px;
  gap: 12px;

  &:before {
    border-color: $c-black-3;
    background: $c-bg;
  }
}

.night-mode .toggle-button--menu .toggle-button__label:before {
  border-color: $c-dark-7;
  background: transparent;
}
</style>
